import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";

import { ReactComponent as Check } from "assets/images/iconsV2/check.svg";
import ChoiceChip from "components/v2/ChoiceChip/ChoiceChip";
import { StopTimeType, StopType } from "types/MultiStop";
import { validateDeliveryDateTime } from "../utils/validation";

export default function StopTypeFormSection({ index }: { index: number }) {
  const intl = useIntl();
  const { setValue, watch, clearErrors, getValues, setError, trigger } = useFormContext();
  const stopType = watch(`schedule.stops.${index}.stop_type`);
  const numberOfStops = watch("schedule.stops")?.length || 0;

  const handleOnChangeType = (value: any) => {
    const stops = getValues("schedule.stops");

    // 1. Clear errors where request requires at least 1 delivery stop
    clearErrors("schedule.multistop.types");

    // 2. Set the stop type
    setValue(`schedule.stops.${index}.stop_type`, value);

    const currentStopTimeType = getValues(`schedule.stops.${index}.stop_time_type`);

    setValue(`schedule.stops.${index}.stop_key`, crypto.randomUUID());

    // 3. If stop time type is BEFORE and user changes to PICKUP, change stop time type to AFTER, and vice versa, to ensure logical consistency of the form
    // And also, unregister the date time fields that are no longer relevant to avoid confusion
    if (currentStopTimeType === StopTimeType.BEFORE && value === StopType.PICKUP) {
      setValue(`schedule.stops.${index}.stop_time_type`, StopTimeType.AFTER);
    }

    if (currentStopTimeType === StopTimeType.AFTER && value === StopType.DROPOFF) {
      setValue(`schedule.stops.${index}.stop_time_type`, StopTimeType.BEFORE);
    }

    // IMPORTANT: Revalidation occurs here!!
    // 4. Retrigger validation for delivery datetime to be after first pickup datetime
    validateDeliveryDateTime({ stops, trigger, index });

    // 5. Check if there is at least 1 delivery stop, if not set error
    if (stops.some((stop: any) => stop.stop_type === StopType.DROPOFF)) return;
    setError("schedule.multistop.types", {
      type: "manual",
      message: intl.formatMessage({
        id: "new-request.schedule.multistop.error.atLeastOneDelivery",
        defaultMessage: "At least one delivery is needed to complete the request",
      }),
    });
  };

  const stopTypeOptions = [
    {
      label: intl.formatMessage({
        id: "general.pickup",
        defaultMessage: "Pickup",
      }),
      value: StopType.PICKUP,
      ...(stopType === StopType.PICKUP && { icon: <Check /> }),
    },
    {
      label: intl.formatMessage({
        id: "general.delivery",
        defaultMessage: "Delivery",
      }),
      value: StopType.DROPOFF,
      ...(stopType === StopType.DROPOFF && { icon: <Check /> }),
      disabled: index === 0,
    },
  ];

  if (numberOfStops <= 2) return null;

  return (
    <>
      <div className="text-secondary-v2 text-sm font-medium mb-sm">
        <FormattedMessage id="new-request.schedule.multistop.stopType" defaultMessage="Stop type" />
      </div>
      <ChoiceChip options={stopTypeOptions} onChange={(value: any) => handleOnChangeType(value)} selected={stopType} />
    </>
  );
}
